import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Your style → Call signs.
// Fixture: `yourStyle` (call sign off). The scene turns the call sign on in
// Report Preferences, sets the format and placement, saves, then overrides the
// call sign for one report in the upload modal's Report Settings.
const FIELD = (key: string) => `[data-pref="${key}"]`;

const scene: Scene = {
  id: "call-signs",
  title: "Call signs",
  startRoute: "/preferences",
  fixture: "yourStyle",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "If your reports refer to you by a unit designator, like one-one, set it once in Report Preferences and the AI writes it in your department's format.",
      actions: [
        { type: "waitFor", target: FIELD("include_call_sign") },
        { type: "scrollIntoView", target: FIELD("include_call_sign") },
        { type: "wait", ms: 1000 },
        { type: "spotlight", target: FIELD("include_call_sign") },
        { type: "wait", ms: 4000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "toggle",
      narration: "Turn on Include Call Sign. The Call Sign, Call Sign Format, and Call Sign Placement fields appear.",
      actions: [
        { type: "wait", ms: 700 },
        { type: "click", target: `${FIELD("include_call_sign")} input[type="checkbox"]` },
        { type: "waitFor", target: `${FIELD("call_sign")} .label > button` },
        { type: "wait", ms: 2000 },
      ],
    },
    {
      id: "number",
      narration: "Type your call sign. I'll use one-one.",
      actions: [
        { type: "click", target: `${FIELD("call_sign")} .label > button` },
        { type: "waitFor", target: `${FIELD("call_sign")} dialog input` },
        { type: "wait", ms: 400 },
        { type: "type", target: `${FIELD("call_sign")} dialog input`, text: "1-1", cps: 4 },
        { type: "wait", ms: 400 },
        { type: "click", target: `${FIELD("call_sign")} .modal-action button:last-child` },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "format",
      narration:
        "The Call Sign Format options are built from your real rank, name, and badge, so you see exactly how each one will read. The badge shows because Include Badge Number is on. I'll take my name, then the call sign in parentheses.",
      actions: [
        { type: "click", target: `${FIELD("call_sign_format")} .dropdown > button` },
        // Options are read out while the sentences go by; the pick lands on
        // "then the call sign in parentheses" (10.5 s of 13.4 s).
        { type: "wait", ms: 1800 },
        { type: "moveTo", target: `${FIELD("call_sign_format")} .dropdown-content li:nth-child(1) p` },
        { type: "wait", ms: 1800 },
        { type: "moveTo", target: `${FIELD("call_sign_format")} .dropdown-content li:nth-child(2) p` },
        { type: "wait", ms: 2400 },
        { type: "click", target: `${FIELD("call_sign_format")} .dropdown-content li:nth-child(3) p` },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "placement",
      narration:
        "Placement decides whether it appears at the first mention only, or at every mention. First mention is enough for me. Then click Save Preferences.",
      actions: [
        { type: "click", target: `${FIELD("call_sign_mention")} .dropdown > button` },
        { type: "wait", ms: 2200 },
        { type: "moveTo", target: `${FIELD("call_sign_mention")} .dropdown-content li:nth-child(2) p` },
        { type: "wait", ms: 1000 },
        { type: "click", target: `${FIELD("call_sign_mention")} .dropdown-content li:nth-child(1) p` },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("preferencesSave") },
        { type: "waitFor", target: '[role="status"]' },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "override",
      narration:
        "Call signs change with the shift. So when you upload, Report Settings shows Call Sign for This Report, already filled in with your default.",
      actions: [
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/narrative-retail-theft.mp3`,
          name: "narrative-retail-theft.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: sel("uploadReportSettings") },
        // Report Settings starts expanded on a fresh modal, so it is pointed at, not clicked.
        { type: "moveTo", target: sel("uploadReportSettings") },
        { type: "waitFor", target: sel("uploadCallSign") },
        { type: "spotlight", target: sel("uploadCallSign") },
        { type: "wait", ms: 2000 },
      ],
    },
    {
      id: "change",
      narration:
        "Change it here, say to two-four, and it applies to this report only. Your saved preference stays one-one.",
      actions: [
        { type: "wait", ms: 500 },
        { type: "spotlight", target: null },
        { type: "click", target: sel("uploadCallSign") },
        { type: "type", target: sel("uploadCallSign"), text: "2-4", replace: true, cps: 4 },
        { type: "wait", ms: 4000 },
      ],
    },
  ],
};

export default scene;
